import {
  DocketItem,
  DocketItemStatus,
  OrderTypeCode,
} from '@oolio-group/domain';

export interface RemovedCourse {
  id: string;
  name: string;
  priority: number;
}

/** Course-calling only exists for dine-in tickets on orgs that have it enabled. */
export const resolveIsCourseCallApplicable = (
  orderTypeCode: OrderTypeCode | undefined,
  session: { kitchenDisplay?: { isCourseCallEnabled?: boolean } } | undefined,
): boolean =>
  orderTypeCode === OrderTypeCode.DINE_IN &&
  !!session?.kitchenDisplay?.isCourseCallEnabled;

const BUMPED_STATUSES: DocketItemStatus[] = [
  DocketItemStatus.BUMPED,
  DocketItemStatus.REBUMPED,
  DocketItemStatus.SERVED,
  DocketItemStatus.VOIDED,
  DocketItemStatus.TRANSFERRED,
];

// With two-level fulfillment on, the first-stage BUMPED status doesn't count as done —
// only the second-stage REBUMPED confirmation does (matches autoBump.ts).
const REBUMP_REQUIRED_STATUSES: DocketItemStatus[] = [
  DocketItemStatus.REBUMPED,
  DocketItemStatus.SERVED,
  DocketItemStatus.VOIDED,
  DocketItemStatus.TRANSFERRED,
];

/** True when every item in the list is done (FR-1). Empty lists are never "fully bumped". */
export const isFullyBumped = (
  items: DocketItem[],
  rebumpToggle: boolean,
): boolean => {
  const doneStatuses = rebumpToggle
    ? REBUMP_REQUIRED_STATUSES
    : BUMPED_STATUSES;
  return (
    items.length > 0 &&
    items.every(item => doneStatuses.includes(item.itemStatus))
  );
};

/** No alert data model exists yet (Allergen Display spec, FR-7); always false until it ships. */
export const hasUnacknowledgedAlert = (_items: DocketItem[]): boolean => false;

/** Org/ticket-level policy that governs course completion — kept as one named object
 * (rather than positional booleans) so call sites like `{ isCourseCallApplicable: true,
 * rebumpToggle: true }` read unambiguously, instead of `(items, true, true)`. */
export interface CourseCompletionPolicy {
  isCourseCallApplicable: boolean;
  rebumpToggle: boolean;
}

/** A course is complete (FR-1) when it has items, is not on HOLD (only checked where
 * course-calling applies), every item is bumped, and it carries no unacknowledged alert. */
export const isCourseRemovable = (
  items: DocketItem[],
  policy: CourseCompletionPolicy,
): boolean => {
  if (!items.length) return false;
  if (policy.isCourseCallApplicable) {
    const isHold = items.some(item => item.course?.callInfo?.isCalled !== true);
    if (isHold) return false;
  }
  if (!isFullyBumped(items, policy.rebumpToggle)) return false;
  return !hasUnacknowledgedAlert(items);
};

export const groupItemsByCourseId = (
  items: DocketItem[],
): Map<string, DocketItem[]> => {
  const groups = new Map<string, DocketItem[]>();
  items.forEach(item => {
    const courseId = item?.course?.id;
    if (!courseId) return;
    const existing = groups.get(courseId) || [];
    existing.push(item);
    groups.set(courseId, existing);
  });
  return groups;
};

/** IDs of removable courses. Must be computed over the full ticket's items, not a
 * partial subset (e.g. one transfer-destination group) — a course can be split across
 * several such subsets. */
export const getRemovableCourseIds = (
  items: DocketItem[],
  policy: CourseCompletionPolicy,
): Set<string> => {
  const byCourseId = groupItemsByCourseId(items);
  const removable = new Set<string>();
  byCourseId.forEach((courseItems, courseId) => {
    if (isCourseRemovable(courseItems, policy)) {
      removable.add(courseId);
    }
  });
  return removable;
};

/** Removed courses in service order (by course.priority), for the View All Courses control (FR-3). */
export const getRemovedCourses = (
  items: DocketItem[],
  policy: CourseCompletionPolicy,
): RemovedCourse[] => {
  const byCourseId = groupItemsByCourseId(items);
  const removableCourseIds = getRemovableCourseIds(items, policy);
  const removed: RemovedCourse[] = [];
  removableCourseIds.forEach(courseId => {
    const courseItems = byCourseId.get(courseId);
    if (!courseItems?.length) return;
    removed.push({
      id: courseId,
      name: courseItems[0]?.course?.name || '',
      priority: courseItems[0]?.course?.priority ?? Number.MAX_SAFE_INTEGER,
    });
  });
  return removed.sort((a, b) => a.priority - b.priority);
};

/** Drops items belonging to a removable course; items with no course are always kept
 * (FR-2). `removableCourseIds` should come from `getRemovableCourseIds` over the full
 * ticket, not just this subset. */
export const filterOutRemovedCourseItems = (
  items: DocketItem[],
  removableCourseIds: Set<string>,
): DocketItem[] => {
  if (!removableCourseIds.size) return items;
  return items.filter(
    item => !item?.course?.id || !removableCourseIds.has(item.course.id),
  );
};

/** Renders removed course names in service order, truncated to the first two plus an overflow count (FR-3). */
export const formatRemovedCourseNames = (courses: RemovedCourse[]): string => {
  const names = courses.map(c => c.name).filter(Boolean);
  if (names.length <= 2) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
};
